"use client";

import { useMemo, useState } from "react";
import {
    Avancer,
    Champ,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    eurosPrecis,
    remplir,
    useParcours,
} from "./Journey";
import { abonnesSaas } from "@/lib/tools/saasSubscribers";
import { expliquerSaas } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { SEUIL_TAXE_CHAMBRE } from "@/lib/tools/microEnterprise";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Combien d'abonnés pour vivre de votre SaaS : du revenu qu'on veut garder au
 * nombre de clients payants.
 *
 * Qui lance un logiciel sur abonnement raisonne en abonnés, pas en chiffre
 * d'affaires. Le calcul vit dans lib/tools/saasSubscribers.js, qui s'appuie
 * sur revenuNet : cotisations, formation et impôt sont exactement ceux du
 * calculateur de revenu net, et la commission de Stripe s'y ajoute.
 *
 * UN ABONNÉ QUI PART DOIT ÊTRE REMPLACÉ. Le résultat ne s'arrête pas au
 * nombre : il dit combien en gagner chaque mois pour s'y maintenir, et en
 * combien de temps on y arrive au rythme choisi.
 *
 * AU-DELÀ DU PLAFOND, AUCUN NOMBRE N'EST DONNÉ comme atteignable en micro : le
 * résultat dit ce que la micro peut laisser au plus, et les curseurs
 * permettent de redescendre.
 *
 * LE STATUT CHANGE LE NOMBRE. La micro cotise sur tout ce qu'elle encaisse ;
 * l'EURL et la SASU déduisent Stripe et les frais, mais cotisent plus lourd.
 * Le résultat compare donc les quatre cas au même prix et pour la même somme,
 * et une ligne touchée devient le calcul affiché.
 */

/** Les cas comparés sur le résultat, dans l'ordre où ils s'affichent. */
const FORMES = [
    { cle: "microBnc", statut: "micro", activite: "bnc" },
    { cle: "microServices", statut: "micro", activite: "services" },
    { cle: "eurl", statut: "eurl", activite: null },
    { cle: "sasu", statut: "sasu", activite: null },
];

const PHASES = {
    statut: 0,
    activite: 0,
    prix: 1,
    objectif: 2,
    frais: 2,
    montantFrais: 2,
    perte: 3,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Votre statut", "Votre prix", "Votre objectif", "Vos résiliations"],
        retour: "Retour",
        continuer: "Continuer",
        voir: "Voir mes abonnés",
        recommencer: "Refaire le calcul",

        qStatut: "Sous quelle forme vendez-vous votre logiciel ?",
        qStatutAide:
            "Les cotisations et l'impôt ne s'y calculent pas de la même façon. Pas encore créé ? Choisissez-en une : le résultat compare les trois.",
        micro: "Micro-entreprise",
        microDetail: "Cotisations sur tout ce que vous encaissez",
        eurl: "EURL",
        eurlDetail: "Gérant indépendant, société à l'impôt sur les sociétés",
        sasu: "SASU",
        sasuDetail: "Président assimilé salarié",

        qActivite: "Comment votre micro-entreprise est-elle déclarée ?",
        qActiviteAide:
            "Un logiciel vendu sur abonnement peut relever de l'une ou de l'autre. Reprenez la catégorie de votre déclaration d'activité : elle fixe vos cotisations et la part de vos recettes qui sera imposée. Pas encore immatriculé ? Faites le calcul avec les deux.",
        bnc: "Activité libérale",
        bncDetail: "Bénéfices non commerciaux (BNC)",
        services: "Prestations de services",
        servicesDetail: "Bénéfices industriels et commerciaux (BIC)",

        qPrix: "Combien coûte votre abonnement par mois ?",
        qPrixAide:
            "Le prix d'un mois, hors taxe. Si vous proposez plusieurs formules, prenez ce que paie en moyenne un abonné. Une formule annuelle se ramène au mois : 290 € par an font 24,17 € par mois.",
        prix: "Prix par mois, hors taxe",
        prixExemple: "Par exemple 29 ou 9,99",
        prixErreur: "Indiquez un prix entre 1 € et 10 000 €, par exemple 29 ou 9,99.",

        qObjectif: "Combien voulez-vous garder chaque mois ?",
        qObjectifAide:
            "Ce qui vous reste une fois Stripe, les cotisations, l'impôt et vos frais payés : l'équivalent de ce que vous vous versez pour vivre.",
        objectif: "Ce que vous voulez garder par mois",

        qFrais: "Votre logiciel vous coûte-t-il quelque chose chaque mois ?",
        qFraisAide:
            "En micro-entreprise, aucun frais ne vient réduire vos cotisations. Ce sont donc vos abonnés qui les paient, en plus de ce que vous voulez garder.",
        fraisNon: "Presque rien",
        fraisNonDetail: "Un hébergement gratuit, des outils offerts",
        fraisOui: "Oui, des abonnements et des services",
        fraisOuiDetail: "Hébergement, base de données, e-mails, nom de domaine",
        qFraisAideSociete:
            "En société, ces frais se déduisent avant les cotisations et l'impôt. Comptez aussi votre expert-comptable et votre banque.",
        fraisOuiDetailSociete: "Hébergement, outils, expert-comptable, banque",
        qMontantFrais: "Combien, en moyenne, par mois ?",
        qMontantFraisAide:
            "Ramenez au mois ce qui ne tombe qu'une fois par an : le nom de domaine, la CFE de décembre, un abonnement payé à l'année.",
        frais: "Frais par mois",

        qPerte: "Chaque mois, quelle part de vos abonnés résilie ?",
        qPerteAide:
            "Sur 100 abonnés au début du mois, combien sont partis à la fin ? Si vous ne le savez pas encore, choisissez une valeur prudente : vous pourrez la changer sur le résultat.",
        perte: "Abonnés qui résilient chaque mois",

        resultatTitre: "Vos abonnés",
        prixCurseur: "Prix de l'abonnement par mois",
        curseursAide: "Déplacez les curseurs : le nombre d'abonnés se recalcule.",
        ilVousFaut: "Il vous faut",
        abonne: "abonné payant",
        abonnes: "abonnés payants",
        detail: "À {prix} par mois, ils vous rapportent {caMois} chaque mois, soit {ca} dans l'année.",
        detailUn: "À {prix} par mois, il vous rapporte {caMois} chaque mois, soit {ca} dans l'année.",
        stripe: "Commissions de Stripe",
        stripeAide: "{commission} sur chaque paiement de {prix}",
        stripeAideSociete: "{commission} sur chaque paiement de {prix}, déduits par la société",
        cotisations: "Cotisations sociales",
        cotisationsEurl: "Cotisations de gérant",
        cotisationsSasu: "Cotisations patronales et salariales",
        impot: "Impôt sur le revenu",
        impotAide: "Pour une personne seule, sans autre revenu.",
        impotAideSociete: "Pour une personne seule, sans autre revenu, sur votre rémunération après l'abattement de 10 %.",
        fraisDeduits: "Déduits par la société",
        cfp: "Contribution à la formation professionnelle",
        fraisLigne: "Vos frais",
        net: "Il vous reste",
        netAide: "{montant} par mois, comme vous le vouliez",
        remunerationEurl: "Soit une rémunération de {montant} par mois avant impôt, cotisations payées par la société.",
        remunerationSasu: "Soit un salaire brut de {montant} par mois.",

        tenirTitre: "À gagner chaque mois, rien que pour rester à {abonnes} abonnés",
        nouveauxUnite: "nouveaux abonnés",
        nouveauUnite: "nouvel abonné",
        tenirAucun: "Personne ne part : chaque nouvel abonné s'ajoute aux autres.",
        rythme: "Nouveaux abonnés par mois",
        atteint: "À ce rythme, vous atteignez {abonnes} abonnés en {delai}.",
        plafonne:
            "À ce rythme, vous plafonnez à {plafond} abonnés : il en part alors autant qu'il en arrive. Pour atteindre {abonnes}, il vous en faut au moins {minimum} nouveaux par mois.",
        mois: "{n} mois",
        unMois: "1 mois",
        an: "1 an",
        ans: "{n} ans",
        anEt: "{ans} et {mois}",

        plafondTitre: "Cet objectif dépasse ce que la micro-entreprise peut vous laisser",
        plafondTexte:
            "Il vous faudrait {abonnes} abonnés, soit {ca} encaissés dans l'année, au-delà du plafond de {plafond}. Dépassé deux années de suite, ce plafond vous fait passer au régime réel, où ce calcul ne vaut plus.",
        plafondMax: "Sous le plafond, vous pouvez avoir au plus {abonnes} abonnés à ce prix. Ils vous laissent {max} par mois.",
        plafondSociete: "Une société, elle, n'a pas de plafond : comparez les statuts plus bas.",

        comparerTitre: "Selon votre statut",
        comparerAide: "Au même prix, pour garder la même somme. Touchez une ligne pour afficher son calcul.",
        microBnc: "Micro-entreprise, activité libérale",
        microServices: "Micro-entreprise, prestations de services",
        comparerAbonnes: "{n} abonnés",
        comparerPlafond: "au-delà du plafond",
        comparerImpossible: "aucun nombre ne suffit",
        votreCalcul: "Affiché",

        impossibleTitre: "À ce prix, un abonné ne vous laisse rien",
        impossibleTexte:
            "La commission de Stripe, {commission} par paiement, et vos cotisations prennent tout ce qu'il vous paie. Montez votre prix pour que chaque abonné vous rapporte quelque chose.",

        tvaFranchise:
            "Vous restez sous {base} de chiffre d'affaires : vos abonnements ne portent pas de TVA, et ce prix est celui que paient vos clients.",
        tvaJanvier:
            "Ce prix s'entend hors taxe : au-delà de {base} encaissés dans l'année, vous facturez la TVA à partir du 1er janvier suivant.",
        tvaImmediate:
            "Ce prix s'entend hors taxe : à ce chiffre d'affaires, vous facturez la TVA dès que vous franchissez {majore} la première année, puis dès le 1er janvier les années suivantes.",
        tvaClients:
            "Un client professionnel récupère cette TVA. Un particulier, lui, paie 20 % de plus, à moins que vous ne baissiez votre prix pour l'absorber. Stripe prend alors sa commission sur le prix toutes taxes comprises.",
        tvaEurope: "Vendre à des particuliers d'autres pays de l'Union obéit à d'autres règles de TVA, que ce calcul ne couvre pas.",

        taxeChambre:
            "S'y ajoute la taxe de chambre de commerce, non chiffrée ici : elle n'est due ni la première année, ni en dessous de {seuil} de chiffre d'affaires.",
        note: "Pour ce calcul, nous avons supposé une personne seule sans autre revenu, impôt au barème, une année pleine sans ACRE, et des abonnements mensuels payés par carte standard de l'Espace économique européen, au tarif public de Stripe. Une carte premium ou émise hors de cet espace coûte davantage. La contribution à la formation n'est pas due la première année.",
        noteSociete:
            "Pour ce calcul, nous avons supposé une personne seule sans autre revenu, impôt au barème, tout le résultat versé en rémunération sans dividendes, et des abonnements mensuels payés par carte standard de l'Espace économique européen, au tarif public de Stripe. Une carte premium ou émise hors de cet espace coûte davantage. En EURL, la société est à l'impôt sur les sociétés et son gérant cotise comme commerçant. En SASU, la cotisation accidents du travail n'est pas comptée : le nombre affiché est un minimum.",
    },
    en: {
        phases: ["Your structure", "Your price", "Your goal", "Your cancellations"],
        retour: "Back",
        continuer: "Continue",
        voir: "See my subscribers",
        recommencer: "Start over",

        qStatut: "In what form do you sell your software?",
        qStatutAide:
            "Contributions and tax are not calculated the same way in each. Not set up yet? Pick one: the result compares all three.",
        micro: "Micro-entreprise",
        microDetail: "Contributions on everything you collect",
        eurl: "EURL",
        eurlDetail: "Self-employed manager, company subject to corporate tax",
        sasu: "SASU",
        sasuDetail: "President treated as an employee",

        qActivite: "How is your micro-entreprise registered?",
        qActiviteAide:
            "Software sold on subscription can fall under either category. Use the one on your business registration: it sets your contributions and the share of your takings that gets taxed. Not registered yet? Run the calculation with both.",
        bnc: "Liberal profession",
        bncDetail: "Non-commercial profits (BNC)",
        services: "Services",
        servicesDetail: "Industrial and commercial profits (BIC)",

        qPrix: "How much does your subscription cost per month?",
        qPrixAide:
            "The price of one month, excluding VAT. If you offer several plans, take what a subscriber pays on average. An annual plan comes down to a month: €290 a year makes €24.17 a month.",
        prix: "Price per month, excluding VAT",
        prixExemple: "For example 29 or 9.99",
        prixErreur: "Enter a price between €1 and €10,000, for example 29 or 9.99.",

        qObjectif: "How much do you want to keep each month?",
        qObjectifAide:
            "What is left once Stripe, contributions, tax and your expenses are paid: the equivalent of what you pay yourself to live on.",
        objectif: "What you want to keep each month",

        qFrais: "Does your software cost you anything each month?",
        qFraisAide:
            "In a micro-entreprise, no expense reduces your contributions. Your subscribers therefore pay for them, on top of what you want to keep.",
        fraisNon: "Next to nothing",
        fraisNonDetail: "Free hosting, free tools",
        fraisOui: "Yes, subscriptions and services",
        fraisOuiDetail: "Hosting, database, email, domain name",
        qFraisAideSociete:
            "In a company, these expenses are deducted before contributions and tax. Include your accountant and your bank too.",
        fraisOuiDetailSociete: "Hosting, tools, accountant, bank",
        qMontantFrais: "How much, on average, per month?",
        qMontantFraisAide:
            "Spread over the months what only comes once a year: the domain name, the business property levy in December, a subscription paid yearly.",
        frais: "Expenses per month",

        qPerte: "Each month, what share of your subscribers cancels?",
        qPerteAide:
            "Out of 100 subscribers at the start of the month, how many have left by the end? If you do not know yet, pick a cautious value: you can change it on the result.",
        perte: "Subscribers who cancel each month",

        resultatTitre: "Your subscribers",
        prixCurseur: "Subscription price per month",
        curseursAide: "Move the sliders: the number of subscribers recalculates.",
        ilVousFaut: "You need",
        abonne: "paying subscriber",
        abonnes: "paying subscribers",
        detail: "At {prix} a month, they bring you {caMois} each month, or {ca} over the year.",
        detailUn: "At {prix} a month, this subscriber brings you {caMois} each month, or {ca} over the year.",
        stripe: "Stripe fees",
        stripeAide: "{commission} on each {prix} payment",
        stripeAideSociete: "{commission} on each {prix} payment, deducted by the company",
        cotisations: "Social contributions",
        cotisationsEurl: "Manager's contributions",
        cotisationsSasu: "Employer and employee contributions",
        impot: "Income tax",
        impotAide: "For a single person with no other income.",
        impotAideSociete: "For a single person with no other income, on your pay after the 10% allowance.",
        fraisDeduits: "Deducted by the company",
        cfp: "Vocational training contribution",
        fraisLigne: "Your expenses",
        net: "You keep",
        netAide: "{montant} a month, as you wanted",
        remunerationEurl: "That is {montant} of pay a month before tax, contributions paid by the company.",
        remunerationSasu: "That is a gross salary of {montant} a month.",

        tenirTitre: "To win each month, just to stay at {abonnes} subscribers",
        nouveauxUnite: "new subscribers",
        nouveauUnite: "new subscriber",
        tenirAucun: "Nobody leaves: every new subscriber adds to the others.",
        rythme: "New subscribers per month",
        atteint: "At this pace, you reach {abonnes} subscribers in {delai}.",
        plafonne:
            "At this pace, you level off at {plafond} subscribers: as many then leave as arrive. To reach {abonnes}, you need at least {minimum} new ones a month.",
        mois: "{n} months",
        unMois: "1 month",
        an: "1 year",
        ans: "{n} years",
        anEt: "{ans} and {mois}",

        plafondTitre: "This goal is more than a micro-entreprise can leave you",
        plafondTexte:
            "You would need {abonnes} subscribers, or {ca} collected over the year, above the {plafond} ceiling. Exceeded two years in a row, this ceiling moves you to the standard regime, where this calculation no longer holds.",
        plafondMax: "Below the ceiling, you can have at most {abonnes} subscribers at this price. They leave you {max} a month.",
        plafondSociete: "A company has no ceiling: compare the structures below.",

        comparerTitre: "By structure",
        comparerAide: "At the same price, to keep the same amount. Tap a row to show its calculation.",
        microBnc: "Micro-entreprise, liberal profession",
        microServices: "Micro-entreprise, services",
        comparerAbonnes: "{n} subscribers",
        comparerPlafond: "above the ceiling",
        comparerImpossible: "no number is enough",
        votreCalcul: "Shown",

        impossibleTitre: "At this price, a subscriber leaves you nothing",
        impossibleTexte:
            "Stripe's fee, {commission} per payment, and your contributions take everything they pay you. Raise your price so that each subscriber brings you something.",

        tvaFranchise:
            "You stay below {base} of turnover: your subscriptions carry no VAT, and this price is what your customers pay.",
        tvaJanvier:
            "This price excludes VAT: above {base} collected in the year, you charge VAT from the following 1 January.",
        tvaImmediate:
            "This price excludes VAT: at this turnover you charge VAT as soon as you pass {majore} in the first year, then from 1 January in the following years.",
        tvaClients:
            "A business customer recovers this VAT. A private customer pays 20% more, unless you lower your price to absorb it. Stripe then takes its fee on the price including VAT.",
        tvaEurope: "Selling to private customers in other EU countries follows other VAT rules, which this calculation does not cover.",

        taxeChambre:
            "The chamber of commerce levy comes on top, not quantified here: it is due neither in the first year, nor below {seuil} of turnover.",
        note: "For this calculation, we have assumed a single person with no other income, tax on the scale, a full year without ACRE, and monthly subscriptions paid by standard European Economic Area card, at Stripe's public rate. A premium card, or one issued outside that area, costs more. The training contribution is not due in the first year.",
        noteSociete:
            "For this calculation, we have assumed a single person with no other income, tax on the scale, the whole result paid out as pay with no dividends, and monthly subscriptions paid by standard European Economic Area card, at Stripe's public rate. A premium card, or one issued outside that area, costs more. In an EURL, the company is subject to corporate tax and its manager contributes as a trader. In a SASU, the workplace accident contribution is not counted: the number shown is a minimum.",
    },
};

/** Un prix saisi, « 29 » ou « 9,99 », en euros ; null s'il ne se lit pas. */
function lirePrix(texte) {
    const v = Number(String(texte).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(v) && v >= 1 && v <= 10000 ? Math.round(v * 100) / 100 : null;
}

/** Un prix d'abonnement s'écrit au centime s'il en porte : 9,99 € ne s'arrondit pas. */
const prixLisible = (v, lang) => (Number.isInteger(v) ? euros(v, lang) : eurosPrecis(v, lang));

/** Un nombre d'abonnés, avec ses espaces de milliers. */
const nombre = (v, lang) => Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

/** Un taux saisi en pour cent : « 7,5 % », "7.5%". */
const taux = (v, lang) => (lang === "fr" ? `${String(v).replace(".", ",")} %` : `${v}%`);

/** Un délai en mois, dit en années au-delà d'un an : « 4 ans et 1 mois ». */
function delaiLisible(mois, t) {
    const enMois = (n) => (n === 1 ? t.unMois : remplir(t.mois, { n }));
    if (mois < 12) return enMois(mois);
    const ans = Math.floor(mois / 12);
    const reste = mois % 12;
    const texteAns = ans === 1 ? t.an : remplir(t.ans, { n: ans });
    return reste ? remplir(t.anEt, { ans: texteAns, mois: enMois(reste) }) : texteAns;
}

/** Zéro prend le singulier en français (« 0 abonné »), le pluriel en anglais. */
const pluriel = (n, lang) => (lang === "fr" ? n > 1 : n !== 1);

/** Une ligne de la cascade : un libellé, une pastille de couleur, un montant retranché. */
function Ligne({ libelle, aide, montant, couleur, lang, reduit, explication, ouvert, onBasculer }) {
    return (
        <div className="py-2.5 border-b border-gray-200/70 dark:border-white/10 last:border-0">
            <div className="flex justify-between items-start gap-4">
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${couleur}`} />
                    <div className="min-w-0">
                        <div className="text-sm dark:text-white">{libelle}</div>
                        {aide && (
                            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">
                                {aide}
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-sm font-semibold tabular-nums dark:text-white shrink-0">
                    −{" "}
                    <MontantExplicable explication={explication} ouvert={ouvert} onBasculer={onBasculer}>
                        <Montant valeur={montant} lang={lang} reduit={reduit} />
                    </MontantExplicable>
                </div>
            </div>
            {ouvert && <PanneauExplication explication={explication} lang={lang} />}
        </div>
    );
}

export default function AbonnesSaas({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [statut, setStatut] = useState(dossier.statut ?? null);
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [prixSaisi, setPrixSaisi] = useState("");
    const [netMensuel, setNetMensuel] = useState(dossier.netMensuel ?? 2500);
    const [fraisMensuels, setFraisMensuels] = useState(dossier.fraisMensuels ?? 0);
    const [pertePct, setPertePct] = useState(5);
    const [nouveauxParMois, setNouveauxParMois] = useState(10);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "statut",
        () => {
            setStatut(null);
            setActivite(null);
            setPrixSaisi("");
            setNetMensuel(2500);
            setFraisMensuels(0);
            setPertePct(5);
            setNouveauxParMois(10);
        },
        PHASES,
        reprendre([
            { etape: "statut", connu: dossier.statut !== undefined },
            ...(dossier.statut === "micro" ? [{ etape: "activite", connu: dossier.activite !== undefined }] : []),
            // Le prix de l'abonnement ne se déduit pas du dossier : le parcours s'y arrête.
            { etape: "prix", connu: false },
            { etape: "objectif", connu: dossier.netMensuel !== undefined },
            { etape: "frais", connu: dossier.fraisMensuels !== undefined },
            ...(dossier.fraisMensuels > 0 ? [{ etape: "montantFrais", connu: true }] : []),
            { etape: "perte", connu: false },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier({ statut, activite, netMensuel, fraisMensuels }, etape === "resultat");

    const prix = lirePrix(prixSaisi);

    const pret = prix && (statut === "eurl" || statut === "sasu" || (statut === "micro" && activite));

    const r = useMemo(
        () =>
            pret
                ? abonnesSaas({ prix, statut, activite, netMensuel, fraisMensuels, pertePct, nouveauxParMois })
                : null,
        [pret, prix, statut, activite, netMensuel, fraisMensuels, pertePct, nouveauxParMois],
    );
    // Chaque nombre du résultat dit d'où il vient : le nombre d'abonnés déplie
    // tout le calcul, chaque ligne de la cascade le sien.
    const ex = useMemo(
        () => (r ? expliquerSaas({ prix, statut, activite, netMensuel, fraisMensuels, pertePct }, r, lang) : null),
        [r, prix, statut, activite, netMensuel, fraisMensuels, pertePct, lang],
    );
    // Les quatre cas au même prix et pour la même somme.
    const comparatif = useMemo(
        () =>
            prix
                ? FORMES.map((f) => ({
                      ...f,
                      r: abonnesSaas({ prix, statut: f.statut, activite: f.activite, netMensuel, fraisMensuels, pertePct }),
                  }))
                : [],
        [prix, netMensuel, fraisMensuels, pertePct],
    );
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    const societe = statut === "eurl" || statut === "sasu";

    if (etape === "statut") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.statut}
                titre={t.qStatut}
                aide={t.qStatutAide}
                choix={["micro", "eurl", "sasu"].map((cle) => ({
                    icone: { micro: "user-rounded-linear", eurl: "case-round-linear", sasu: "buildings-2-linear" }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setStatut(cle);
                        if (cle === "micro") {
                            aller("activite");
                            return;
                        }
                        setActivite(null);
                        aller("prix");
                    },
                }))}
            />
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={t.qActiviteAide}
                choix={["bnc", "services"].map((cle) => ({
                    icone: cle === "bnc" ? "code-square-linear" : "server-square-linear",
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setActivite(cle);
                        aller("prix");
                    },
                }))}
            />
        );
    }

    if (etape === "prix") {
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.prix} titre={t.qPrix} aide={t.qPrixAide}>
                <Champ
                    libelle={t.prix}
                    valeur={prixSaisi}
                    poser={setPrixSaisi}
                    inputMode="decimal"
                    placeholder={t.prixExemple}
                    erreur={prixSaisi.trim() && !prix ? t.prixErreur : null}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && prix) aller("objectif");
                    }}
                />
                <Avancer onClick={() => aller("objectif")} disabled={!prix}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "objectif") {
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.objectif} titre={t.qObjectif} aide={t.qObjectifAide}>
                <Curseur
                    libelle={t.objectif}
                    valeur={netMensuel}
                    poser={setNetMensuel}
                    minimum={500}
                    maximum={8000}
                    pas={100}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("frais")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "frais") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.frais}
                titre={t.qFrais}
                aide={societe ? t.qFraisAideSociete : t.qFraisAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.fraisNon,
                        detail: t.fraisNonDetail,
                        onClick: () => {
                            setFraisMensuels(0);
                            aller("perte");
                        },
                    },
                    {
                        icone: "bill-list-linear",
                        titre: t.fraisOui,
                        detail: societe ? t.fraisOuiDetailSociete : t.fraisOuiDetail,
                        onClick: () => {
                            setFraisMensuels(100);
                            aller("montantFrais");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "montantFrais") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montantFrais}
                titre={t.qMontantFrais}
                aide={t.qMontantFraisAide}
            >
                <Curseur
                    libelle={t.frais}
                    valeur={fraisMensuels}
                    poser={setFraisMensuels}
                    maximum={2000}
                    pas={10}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("perte")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "perte") {
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.perte} titre={t.qPerte} aide={t.qPerteAide}>
                <Curseur
                    libelle={t.perte}
                    valeur={pertePct}
                    poser={setPertePct}
                    maximum={20}
                    pas={0.5}
                    formater={(v) => taux(v, lang)}
                />
                <Avancer onClick={() => aller("resultat")}>{t.voir}</Avancer>
            </Ecran>
        );
    }

    const curseurs = (
        <div className="mt-4 space-y-5">
            <Curseur
                libelle={t.prixCurseur}
                valeur={prix}
                poser={(v) => setPrixSaisi(String(v))}
                minimum={1}
                maximum={300}
                pas={1}
                formater={(v) => prixLisible(v, lang)}
            />
            <Curseur
                libelle={t.objectif}
                aide={t.curseursAide}
                valeur={netMensuel}
                poser={setNetMensuel}
                minimum={500}
                maximum={8000}
                pas={100}
                formater={(v) => euros(v, lang)}
            />
        </div>
    );

    if (!r.possible) {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
                {curseurs}
                <div className="mt-6 rounded-xl p-5 border border-amber-500/50 bg-amber-500/[0.06]">
                    <div className="font-semibold text-amber-700 dark:text-amber-400 leading-snug">
                        {t.impossibleTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(t.impossibleTexte, { commission: eurosPrecis(r.commissionParAbonne, lang) })}
                    </p>
                </div>
                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </Resultat>
        );
    }

    const messageTva = {
        franchise: remplir(t.tvaFranchise, { base: euros(r.tva.base, lang) }),
        janvier: remplir(t.tvaJanvier, { base: euros(r.tva.base, lang) }),
        immediate: remplir(t.tvaImmediate, { majore: euros(r.tva.majore, lang) }),
    }[r.tva.statut];

    const n = nombre(r.abonnes, lang);

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {curseurs}

            {r.depassePlafond ? (
                <div className="mt-6 rounded-xl p-5 border border-amber-500/50 bg-amber-500/[0.06]">
                    <div className="font-semibold text-amber-700 dark:text-amber-400 leading-snug">
                        {t.plafondTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(t.plafondTexte, {
                            abonnes: n,
                            ca: euros(r.chiffreAffaires, lang),
                            plafond: euros(r.plafond, lang),
                        })}
                    </p>
                    <p className="text-sm font-semibold dark:text-white mt-3 mb-0">
                        {remplir(t.plafondMax, {
                            abonnes: nombre(r.abonnesAuPlafond, lang),
                            max: euros(r.netMensuelMaximum, lang),
                        })}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{t.plafondSociete}</p>
                </div>
            ) : (
                <>
                    <div className="mt-6 rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">{t.ilVousFaut}</div>
                        <div className="text-3xl font-bold tabular-nums dark:text-white">
                            <MontantExplicable
                                explication={ex?.abonnes}
                                ouvert={ouverte === "abonnes"}
                                onBasculer={() => basculer("abonnes")}
                            >
                                <Montant valeur={r.abonnes} lang={lang} reduit={reduit} formater={nombre} />
                            </MontantExplicable>{" "}
                            <span className="text-base font-semibold text-gray-500 dark:text-lightBlue/70">
                                {pluriel(r.abonnes, lang) ? t.abonnes : t.abonne}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                            {remplir(r.abonnes === 1 ? t.detailUn : t.detail, {
                                prix: prixLisible(prix, lang),
                                caMois: euros(r.chiffreAffairesMensuel, lang),
                                ca: euros(r.chiffreAffaires, lang),
                            })}
                        </div>
                        {ouverte === "abonnes" && <PanneauExplication explication={ex?.abonnes} lang={lang} />}
                    </div>

                    <IndiceExplication lang={lang} />

                    <div className="mt-3">
                        <Ligne
                            libelle={t.stripe}
                            aide={remplir(societe ? t.stripeAideSociete : t.stripeAide, {
                                commission: eurosPrecis(r.commissionParAbonne, lang),
                                prix: prixLisible(prix, lang),
                            })}
                            montant={r.commissions}
                            couleur="bg-indigo-500"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.commissions}
                            ouvert={ouverte === "commissions"}
                            onBasculer={() => basculer("commissions")}
                        />
                        <Ligne
                            libelle={{ micro: t.cotisations, eurl: t.cotisationsEurl, sasu: t.cotisationsSasu }[statut]}
                            montant={r.cotisations}
                            couleur="bg-primary"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.cotisations}
                            ouvert={ouverte === "cotisations"}
                            onBasculer={() => basculer("cotisations")}
                        />
                        <Ligne
                            libelle={t.impot}
                            aide={societe ? t.impotAideSociete : t.impotAide}
                            montant={r.impot}
                            couleur="bg-amber-500"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.impot}
                            ouvert={ouverte === "impot"}
                            onBasculer={() => basculer("impot")}
                        />
                        {!societe && (
                            <Ligne
                                libelle={t.cfp}
                                montant={r.cfp}
                                couleur="bg-gray-400 dark:bg-lightBlue/40"
                                lang={lang}
                                reduit={reduit}
                                explication={ex?.cfp}
                                ouvert={ouverte === "cfp"}
                                onBasculer={() => basculer("cfp")}
                            />
                        )}
                        {r.frais > 0 && (
                            <Ligne
                                libelle={t.fraisLigne}
                                aide={societe ? t.fraisDeduits : null}
                                montant={r.frais}
                                couleur="bg-slate-500 dark:bg-slate-400"
                                lang={lang}
                                reduit={reduit}
                                explication={ex?.frais}
                                ouvert={ouverte === "frais"}
                                onBasculer={() => basculer("frais")}
                            />
                        )}
                    </div>

                    <div className="mt-6 rounded-xl p-5 border border-green-600/40 bg-green-600/[0.06]">
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">{t.net}</div>
                        <div className="text-3xl font-bold tabular-nums text-green-700 dark:text-green-400">
                            <MontantExplicable explication={ex?.net} ouvert={ouverte === "net"} onBasculer={() => basculer("net")}>
                                <Montant valeur={r.net} lang={lang} reduit={reduit} />
                            </MontantExplicable>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                            {remplir(t.netAide, { montant: euros(r.netMensuelObtenu, lang) })}
                        </div>
                        {societe && (
                            <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                                {remplir(statut === "eurl" ? t.remunerationEurl : t.remunerationSasu, {
                                    montant: euros(r.remuneration / 12, lang),
                                })}
                            </div>
                        )}
                        {ouverte === "net" && <PanneauExplication explication={ex?.net} lang={lang} />}
                    </div>

                    <div className="mt-6 rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                            {remplir(t.tenirTitre, { abonnes: n })}
                        </div>
                        <div className="text-2xl font-bold tabular-nums dark:text-white">
                            <MontantExplicable
                                explication={ex?.nouveaux}
                                ouvert={ouverte === "nouveaux"}
                                onBasculer={() => basculer("nouveaux")}
                            >
                                <Montant valeur={r.nouveauxPourTenir} lang={lang} reduit={reduit} formater={nombre} />
                            </MontantExplicable>{" "}
                            <span className="text-sm font-semibold text-gray-500 dark:text-lightBlue/70">
                                {pluriel(r.nouveauxPourTenir, lang) ? t.nouveauxUnite : t.nouveauUnite}
                            </span>
                        </div>
                        {pertePct === 0 && (
                            <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">{t.tenirAucun}</div>
                        )}
                        {ouverte === "nouveaux" && <PanneauExplication explication={ex?.nouveaux} lang={lang} />}

                        <div className="mt-5 space-y-5">
                            <Curseur
                                libelle={t.perte}
                                valeur={pertePct}
                                poser={setPertePct}
                                maximum={20}
                                pas={0.5}
                                formater={(v) => taux(v, lang)}
                            />
                            <Curseur
                                libelle={t.rythme}
                                valeur={nouveauxParMois}
                                poser={setNouveauxParMois}
                                minimum={1}
                                maximum={200}
                                pas={1}
                                formater={(v) => nombre(v, lang)}
                            />
                        </div>
                        <p className="text-sm font-semibold dark:text-white mt-4 mb-0">
                            {r.delai.atteint
                                ? remplir(t.atteint, { abonnes: n, delai: delaiLisible(r.delai.mois, t) })
                                : remplir(t.plafonne, {
                                      plafond: nombre(r.delai.plafond, lang),
                                      abonnes: n,
                                      minimum: nombre(r.nouveauxPourAtteindre, lang),
                                  })}
                        </p>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-5 mb-0">
                        {messageTva}
                        {r.tva.statut !== "franchise" && ` ${t.tvaClients}`} {t.tvaEurope}
                    </p>
                </>
            )}

            <div className="mt-6">
                <div className="text-sm font-semibold dark:text-white">{t.comparerTitre}</div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-3">{t.comparerAide}</p>
                <div className="rounded-xl border border-gray-200/70 dark:border-white/10 overflow-hidden">
                    {comparatif.map((f) => {
                        const affiche = f.statut === statut && (f.statut !== "micro" || f.activite === activite);
                        const valeur = !f.r?.possible
                            ? t.comparerImpossible
                            : f.r.depassePlafond
                              ? t.comparerPlafond
                              : remplir(t.comparerAbonnes, { n: nombre(f.r.abonnes, lang) });
                        return (
                            <button
                                key={f.cle}
                                type="button"
                                onClick={() => {
                                    setStatut(f.statut);
                                    setActivite(f.activite);
                                }}
                                aria-pressed={affiche}
                                className={`w-full flex items-center justify-between gap-4 px-4 py-3 text-left border-b last:border-0 border-gray-200/70 dark:border-white/10 transition-colors ${
                                    affiche ? "bg-primary/[0.06]" : "hover:bg-gray-50 dark:hover:bg-white/[0.03]"
                                }`}
                            >
                                <span className="text-sm dark:text-white min-w-0">
                                    {t[f.cle]}
                                    {affiche && (
                                        <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-[#D04515] dark:text-[#FF8A5B]">
                                            {t.votreCalcul}
                                        </span>
                                    )}
                                </span>
                                <span
                                    className={`text-sm tabular-nums shrink-0 ${
                                        f.r?.possible && !f.r.depassePlafond
                                            ? "font-semibold dark:text-white"
                                            : "text-gray-500 dark:text-lightBlue/70"
                                    }`}
                                >
                                    {valeur}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {r.taxeChambreDue && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                    {remplir(t.taxeChambre, { seuil: euros(SEUIL_TAXE_CHAMBRE, lang) })}
                </p>
            )}
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{societe ? t.noteSociete : t.note}</p>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
